/* eslint-disable max-lines */
import orderBy from 'lodash-es/orderBy'
import moment from 'moment-timezone'
import { addCurrency } from '@/utils/currency.utils'
import { CartResetType, DoResetProps } from '@/hooks/useReset'
import { GeneralFns } from '@/types/store.types'
import { META_ADDONS } from '@/config/ticketure-metadata.config'
import { PaymentMethodType } from '@/types/general.types'
import { ShippingDetailsT } from '@/types/CheckoutProvider.types'
import { initialCartId } from '@/config/cart.config'
import { getCategoryQty } from '@/utils/components/tickets.utils'
import TXT from '@/messages'

type GetLineItemsByCategoryProps = {
  lineItems: readonly Transformed.LineItem[]
}

const NON_RECURRING_EVENT_CATEGORIES: Transformed.EventCategory[] = ['Community', 'Music', 'Workshops']

/**
 * Sorts the timeslots within each line item based on their start time in ascending order.
 *
 * @param {LocalLineItemWithMeta[]} lineItems - An array of line items, each containing a collection of timeslots.
 * @returns {LocalLineItemWithMeta[]} A new array with the timeslots in each line item sorted by their start time.
 */
const sortTimeslots = (lineItems: LocalLineItemWithMeta[]): LocalLineItemWithMeta[] => lineItems.map((item) => ({
  ...item,
  items: orderBy(item.items, [(innerItem: Transformed.LineItem) => (
    new Date(innerItem.timeslot?.startTime ?? 0).getTime()
  )]),
}))

/**
 * Sorts an array of line items by the start time of their first timeslot in ascending order.
 *
 * This function first organizes the line items by internally sorted timeslots, then
 * arranges the line items based on the numeric timestamp of the `startTime` property
 * associated with the first timeslot of each item. If a timeslot is not present or the
 * start time is unavailable, it applies a default timestamp of `0`.
 *
 * @param {LocalLineItemWithMeta[]} lineItems - The array of line items to be sorted. Each item
 * contains a collection of sub-items with timeslot data to evaluate.
 *
 * @returns {LocalLineItemWithMeta[]} A new array of line items ordered by the start time
 * of their first timeslot.
 */
const sortByFirstTimeslot = (lineItems: LocalLineItemWithMeta[]): LocalLineItemWithMeta[] => (
  orderBy(sortTimeslots(lineItems), [(item: LocalLineItemWithMeta) => (
    new Date(item.items[0]?.timeslot?.startTime ?? 0).getTime()
  )])
)

const sortByCategory = (items: LocalLineItemWithMeta[]): LocalLineItemWithMeta[] => orderBy(items, 'category.title')

const itemEventIsNonRecurring = (item: LocalLineItemWithMeta): boolean => (
  NON_RECURRING_EVENT_CATEGORIES.includes(item.event.category as Transformed.EventCategory)
)

/**
 * Groups line items by category and separates them into addOns, tickets (Admission/DayPass),
 * and events (Music/Community/Workshops).
 * Note: event items are sorted by timeslot.
 */
export const getLineItemsByCategory = ({ lineItems }: GetLineItemsByCategoryProps): {
  addOns: LocalLineItemWithMeta[]
  tickets: LocalLineItemWithMeta[]
  events: LocalLineItemWithMeta[]
} => {
  const lineItemWithMetaMap = new Map<string, LocalLineItemWithMeta>()

  lineItems.forEach((lineItem) => {
    const { category, tier, event } = lineItem
    const found = lineItemWithMetaMap.get(category.id)
    if (found) {
      const tiersSet = new Set<Transformed.TicketTier>()
      const items = new Set<Transformed.LineItem>()

      lineItemWithMetaMap.delete(category.id)

      found.tiers.forEach((t) => tiersSet.add(t))
      tiersSet.add(tier)

      found.items.forEach((item) => items.add(item))
      items.add(lineItem)

      lineItemWithMetaMap.set(category.id, {
        event,
        category,
        tiers: [...tiersSet.values()],
        items: [...items.values()],
      })
    } else {
      lineItemWithMetaMap.set(category.id, {
        event,
        category,
        tiers: [tier],
        items: [lineItem],
      })
    }
  })

  const sortedLineItems = sortByCategory([...lineItemWithMetaMap.values()])

  return {
    addOns: sortedLineItems.filter((item) => item.event.category === 'AddOn'),
    tickets: sortedLineItems.filter((item) => item.event.category === 'Admission' || item.event.category === 'DayPass'),
    events: sortByFirstTimeslot(sortedLineItems.filter(itemEventIsNonRecurring)),
  }
}

/**
 * Checks whether a cart exists and its ID is not equal to the initial cart ID.
 * @param {Cart | string | undefined} cart - The cart object, ID string, or null/undefined.
 * @returns {boolean} Returns true if the cart exists and its ID is not equal to the initial cart ID; false otherwise.
 */
export const cartExists = (cart?: Transformed.Cart | string): boolean => {
  if (typeof cart === 'string') {
    return !!cart && cart !== initialCartId
  }
  return !!cart?.id && cart.id !== initialCartId
}

/**
 * Retrieves the promotional codes applied to the given cart.
 *
 * This function extracts and returns the order-level promotional codes
 * associated with the provided cart. If the cart is undefined or does
 * not contain order-level promotional codes, it returns an empty array.
 *
 * @param {Cart | undefined} cart - The cart object from which promotional codes are to be retrieved.
 * @returns {PromoCode[]} An array of promotional codes applied to the cart.
 * Returns an empty array if no codes are found.
 */
export const getPromoCodes = (cart: Transformed.Cart | undefined): Transformed.PromoCode[] => {
  if (cart && cart.orderLevelPromoCodes) {
    return cart.orderLevelPromoCodes as Transformed.PromoCode[]
  }
  return []
}

/**
 * Retrieves a list of valid promo codes from the provided cart.
 *
 * This function processes the given cart to extract promo codes, ensuring
 * they are trimmed of leading and trailing whitespace and filtered to exclude
 * any empty or invalid promo code entries.
 *
 * @param {Transformed.Cart | undefined} cart - The shopping cart object from which
 *        promo codes are extracted. Could be undefined if no cart is provided.
 * @returns {string[]} An array of valid promo codes associated with the cart. If no promo
 *          codes are found or the cart is undefined, returns an empty array.
 */
export const getPromoCodeList = (cart: Transformed.Cart | undefined): string[] => (
  getPromoCodes(cart).map((item) => item.promoCode.trim())
    .filter((item) => item)
)

/**
 * We don't apply gift cards until checkout
 * Between gift card submission and then we mock the adjustment to
 * the grand total
 *
 * { appliedAmount: number, balances: [{ number: string, remaining: number }] }
 */
export const mockApplyGiftCards = (
  cart: Transformed.Cart,
  giftCards: Transformed.GiftCardBalance[] | null,
): Transformed.AppliedGiftCardTotals => {
  let appliedAmount = 0
  const balances: Transformed.AppliedGiftCardBalance[] = []
  const { grandTotal } = cart
  let adjustedGrandTotal = grandTotal

  if (grandTotal > 0 && giftCards) {
    giftCards.forEach((giftCard) => {
      const applied = adjustedGrandTotal > giftCard.balance
        ? giftCard.balance
        : parseFloat((grandTotal - appliedAmount).toFixed(2))

      adjustedGrandTotal = parseFloat((adjustedGrandTotal - applied).toFixed(2))

      appliedAmount = parseFloat((appliedAmount + applied).toFixed(2))

      balances.push({
        id: giftCard.number,
        remaining: parseFloat((giftCard.balance - applied).toFixed(2)),
      })
    })
  }

  return {
    appliedAmount,
    balances,
  }
}

/**
 * Get the total discount from promo codes
 * used in Checkout.tsx and PurchaseConfirmation.utils.ts
 * @param {Cart | undefined} cart
 * @returns {number}
 */
export const getPromoCodeDiscount = (cart: Transformed.Cart | undefined): number => {
  const codes = getPromoCodes(cart)
  return codes.reduce((prev, next) => addCurrency(prev, next.discountAmount), 0)
}

/**
 * Get seconds till expiration from the provided expires at date string
 * @param {string | null} expiresAt - date string
 * @param {moment.Moment} now - now moment
 * @returns {number} seconds till expiration
 */
export const getExpiresInFromExpiresAt = (expiresAt: string | undefined, now: moment.Moment): number => (
  expiresAt
    ? moment(expiresAt).diff(now, 'seconds')
    : 0
)

/**
 * Retrieves the non-discounted cart modifications from the given cart.
 *
 * This function filters the cart modifications in the provided cart object
 * to return only those that do not have any discount applied.
 *
 * @param {Cart} cart - The cart object containing a list of cart modifications.
 * @returns {CartMod[]} An array of cart modifications where the discount is 0.
 */
export const getNonDiscountCartMod = (cart: Transformed.Cart): Transformed.CartMod[] => (
  cart.cartMod
    .filter((mod: Transformed.CartMod | null) => mod && mod.discount === 0) as Transformed.CartMod[]
)

/**
 * Calculates the subtotal by summing up the cart value and the amounts of a list of line items,
 * while subtracting the amounts of line items marked for deletion.
 *
 * @param {number | null} cartValue - The current subtotal of the cart or null if no cart value exists.
 * @param {Transformed.LineItem[] | undefined} items - An array of line items to include in the subtotal calculation.
 * @param {Transformed.LineItem[] | undefined} lineItemsToDelete - An array of line items to exclude (subtract)
 * from the subtotal calculation.
 * @returns {number} - The calculated subtotal considering the cart value, items, and items to delete.
 */
export const getSubtotals = (
  cartValue: number | null,
  items: Transformed.LineItem[] | undefined,
  lineItemsToDelete: Transformed.LineItem[] | undefined,
): number => {
  let value = 0
  /* istanbul ignore else */
  if (items) {
    items.forEach((item: Transformed.LineItem): void => {
      const price = item.amount
      value += price
    })
  }
  /* istanbul ignore else */
  if (lineItemsToDelete) {
    lineItemsToDelete.forEach((item: Transformed.LineItem): void => {
      value -= item.amount
    })
  }

  // add cart subtotal / total
  /* istanbul ignore else */
  if (cartValue) {
    value += cartValue
  }

  return value
}

/**
 * Given a cart, returns the subtotal (before tax) of the cart
 * @param cart {Cart} - the current cart
 * @param options - adjustments to the subtotal amount
 * @param options.withAddOns {boolean} - Include add-ons in subtotal?
 * @param options.withDiscount {boolean} - Include discounts in subtotal?
 */
export const getCartSubtotal = (
  cart: Transformed.Cart,
  options: {
    withAddOns: boolean;
    withDiscount: boolean;
  },
): number => {
  const ticketsPrice = cart.lineItems
    .filter((item) => (options.withAddOns ? true : item.event.category !== 'AddOn'))
    .map((item) => item.amount)
    .reduce((acc, lineItemPrice) => acc + lineItemPrice, 0)

  const promoCodeDiscount = (options.withDiscount && cart.promoCodeDiscount) || 0
  return ticketsPrice - promoCodeDiscount
}

/**
 * Extracts and returns an array of all ticket IDs from the provided line items.
 *
 * @param {LocalLineItemWithMeta[]} lineItems - An array of line items, each containing metadata.
 * @returns {string[]} An array of ticket IDs extracted from all items within the line items.
 */
export const getAllLineItemIds = (lineItems: LocalLineItemWithMeta[]): string[] => (
  lineItems.reduce((ids, group) => {
    let newIds = ids
    group.items.forEach((item) => {
      newIds = [...newIds, ...item.ticketIds as string[]]
    })
    return newIds
  }, [] as string[])
)
/**
 * Asynchronously handles the deletion of an entire order history associated with a given cart.
 * It categorizes the line items from the cart into tickets, add-ons, and events, and then deletes
 * all these items. If there are items to delete, it performs a deletion operation using the
 * provided `deleteTickets` function. If there are no items to delete, it resets the cache.
 *
 * @param {Cart} cart - The cart object containing line items to be deleted.
 * @param {(ticketIds: string[]) => Promise<void>} deleteTickets - Function to call for deleting tickets.
 * @param {(props: DoResetProps) => Promise<void>} doReset
 * @param {GeneralFns['updatePartial']} updatePartial
 * @returns {Promise<void>} A promise that resolves when the deletion process is complete.
 */
export const handleDeleteEntireOrderHistory = async (
  cart: Transformed.Cart,
  deleteTickets: (ticketIds: string[]) => Promise<void>,
  doReset: (props: DoResetProps) => Promise<void>,
  updatePartial: GeneralFns['updatePartial'],
): Promise<void> => {
  const {
    tickets: cartTicketsWithMeta,
    addOns: cartAddOnsWithMeta,
    events: cartEventsWithMeta,
  } = getLineItemsByCategory({ lineItems: cart.lineItems })
  const allTicketIds = getAllLineItemIds(cartTicketsWithMeta)
  const allAddOnIds = getAllLineItemIds(cartAddOnsWithMeta)
  const allEventTicketIds = getAllLineItemIds(cartEventsWithMeta)
  const allIds = [...allTicketIds, ...allAddOnIds, ...allEventTicketIds]

  // Move line items to lineItemsToDelete for tracking
  updatePartial({ lineItemsToDelete: cart.lineItems as Transformed.LineItem[] })

  if (allIds.length > 0) {
    await deleteTickets(allIds)
  } else {
    await doReset({ type: CartResetType.LocalItemsAndCart })
  }
}

/**
 * Is checkout blocked?
 */
export const checkoutBlocked = (cart: Transformed.Cart): boolean => {
  if (cart.cartMod.length === 0) return false
  return !!cart.cartMod.find((mod) => mod.blockingCheckout)
}

/**
 * create Announcement[] from cart mods that are blocking checkout
 */
export const checkoutBlockedAnnouncements = (cart: Transformed.Cart): Transformed.Announcement[] => (
  checkoutBlocked(cart)
    ? cart.cartMod.map((mod: Nullable<Transformed.CartMod>) => {
      /* istanbul ignore else */
      if (mod && mod.blockingCheckout) {
        return {
          id: 'code-required-announcement',
          message: TXT.checkoutMessages.codeRequiredFor(mod.name),
          code: 'checkout-blocked',
          priority: 'High',
        } satisfies Transformed.Announcement
      }
      return null
    }).filter((mod) => !!mod)
    : [] as Transformed.Announcement[]
)

/**
 * Returns ticket ids and the corresponding ticket slug that are blocked from checkout by a cartMod
 * @param cart {Transformed.Cart} - the active cart
 */
export const getBlockedTickets = (cart: Transformed.Cart): {
  blockedTicketIds: string[],
  blockedTicketSlug: string,
} => {
  const blockedTicketIds = cart.cartMod
    .filter((mod) => mod?.blockingCheckout)
    .map((mod) => mod?.ticketsAffected)
    .flat() as string[]

  // As blocked tickets are handled immediately, there should only ever be 1 relevant event slug
  const blockedTicketSlug = cart.lineItems
    .filter((lineItem) => lineItem.ticketIds.includes(blockedTicketIds[0]))
    .map((lineItem) => lineItem.event.slug)[0]

  return {
    blockedTicketIds,
    blockedTicketSlug,
  }
}

/**
 * Merges two arrays of line items by combining items with the same `tier.id`
 * and summing their quantities and ticket IDs.
 *
 * @param {Transformed.LineItem[]} itemsA - The first array of line items to merge.
 * @param {Transformed.LineItem[]} itemsB - The second array of line items to merge.
 * @returns {Transformed.LineItem[]} A new array of line items, where items with the same `tier.id` are combined.
 */
const mergeLocalLineItems = (
  itemsA: readonly Transformed.LineItem[],
  itemsB: readonly Transformed.LineItem[],
): Transformed.LineItem[] => {
  const merged: Transformed.LineItem[] = []
  itemsA.forEach((item) => merged.push(item))
  itemsB.forEach((item) => {
    const found = merged.find((existing) => existing.tier.id === item.tier.id)
    if (found) {
      const foundIdx = merged.indexOf(found)
      merged.splice(foundIdx, 1)
      merged.push({
        ...found,
        quantity: found.quantity + item.quantity,
        ticketIds: [...found.ticketIds, ...item.ticketIds],
      })
    } else {
      merged.push(item)
    }
  })
  return merged
}

/**
 * Adds a `LocalLineItemWithMeta` item to a map, either creating a new entry
 * or merging with an existing one if the `category.id` of the item matches an
 * existing entry in the map.
 *
 * The merging process includes:
 * - Combining the tiers of the existing entry and the new item into a unique set.
 * - Merging the `items` property of both the existing entry and the new item
 *   by using the `mergeLocalLineItems` function.
 * - Replacing the existing entry in the map with the updated merged data.
 *
 * @param mergedMap - A `Map` where each key represents a `category.id` and the corresponding
 * value is an object of type `LocalLineItemWithMeta`.
 * @param item - An object of type `LocalLineItemWithMeta` to be added to or merged into the map.
 * @returns The updated map with the new or merged item.
 */
const addItemToMergeMap = (
  mergedMap: Map<string, LocalLineItemWithMeta>,
  item: LocalLineItemWithMeta,
): Map<string, LocalLineItemWithMeta> => {
  const found = mergedMap.get(item.category.id)
  if (found) {
    // combine tiers
    const tiers = new Set<Transformed.TicketTier>()
    found.tiers.forEach((tier) => tiers.add(tier))
    item.tiers.forEach((tier) => tiers.add(tier))

    // remove found and replace with updated item
    // with combined tiers, and merged items
    mergedMap.delete(item.category.id)
    mergedMap.set(item.category.id, {
      ...item,
      tiers: [...tiers],
      items: mergeLocalLineItems(found.items, item.items),
    })
  } else {
    mergedMap.set(item.category.id, item)
  }
  return mergedMap
}

/**
 * Merges local line items and cart line items into a unified collection,
 * while excluding specific line items marked for deletion.
 *
 * This function performs the following steps:
 * - Combines local and cart line items, avoiding duplication.
 * - Removes line items specified in the `lineItemsToDelete` parameter based on
 *   matching category, tier, and quantity.
 * - Updates tier information by filtering out tiers associated with removed items.
 *
 * @param {LocalLineItemWithMeta[]} localLineItems - An array of line items representing items stored locally.
 * @param {LocalLineItemWithMeta[]} cartLineItems - An array of line items representing items in the user's cart.
 * @param {Transformed.LineItem[]} lineItemsToDelete - An array of line items that should be removed
 * from the merged collection.
 * @returns {LocalLineItemWithMeta[]} A combined array of local and cart line items with deletions applied.
 */
export const mergeLocalAndCartLineItems = (
  localLineItems: LocalLineItemWithMeta[],
  cartLineItems: LocalLineItemWithMeta[],
  lineItemsToDelete: Transformed.LineItem[],
): LocalLineItemWithMeta[] => {
  let mergedMap = new Map<string, LocalLineItemWithMeta>()
  localLineItems.forEach((item) => {
    mergedMap = addItemToMergeMap(mergedMap, item)
  })
  cartLineItems.forEach((item) => {
    mergedMap = addItemToMergeMap(mergedMap, item)
  })
  lineItemsToDelete.forEach((item) => {
    const found = mergedMap.get(item.category.id)
    /* istanbul ignore else */
    if (found) {
      mergedMap.delete(item.category.id)

      // filter out items we want to delete
      const nextItems: Transformed.LineItem[] = found.items.filter((ticket) => (
        !(ticket.tier.id === item.tier.id && item.quantity === ticket.quantity)
      ))

      /* istanbul ignore else */
      if (nextItems.length > 0) {
        // removing extra tiers
        const tierIds = nextItems.map((nextItem) => nextItem.tier.id)
        const nextTiers = found.tiers.filter((tier) => tierIds.includes(tier.id))
        mergedMap.set(item.category.id, {
          ...found,
          tiers: nextTiers,
          items: nextItems,
        })
      }
    }
  })
  return [...mergedMap.values()]
}

/**
 * Determines whether the provided metadata represents an event add-on.
 *
 * This function evaluates the given metadata object to check if it matches
 * the criteria for being identified as an event add-on. The metadata
 * is considered an event add-on if its `metakey` equals the constant `META_ADDONS`
 * and its `value` equals `'1'`.
 *
 * @param {Nullable<Transformed.MetaData>} meta - The metadata object to evaluate. It may be undefined or null.
 * @returns {boolean} `true` if the metadata represents an event add-on; otherwise, `false`.
 */
export const isEventAddOn = (meta: Nullable<Transformed.MetaData>): boolean => (
  meta?.metakey === META_ADDONS && meta?.value === '1'
)

/*
* Checks if the cart contains any line items that are categorized as 'MEMBER'.
*
* This function takes a cart and a list of local line items, combines them, and checks if any of the line items
* belong to the 'MEMBER' category. It returns true if at least one line item is found with the 'MEMBER' visibility;
* otherwise, it returns false.
*
* @param {Cart} cart - The cart object containing line items to be checked.
* @param {LineItem[]} localLineItems - An array of local line items to be included in the check.
* @returns {boolean} Returns true if any line item in the combined list has a category visibility of 'MEMBER';
* false otherwise.
*/
export const cartHasMemberTickets = (
  cart: Transformed.Cart,
  localLineItems: Transformed.LineItem[],
): boolean => [...cart.lineItems, ...localLineItems]
  .some((li) => li.category.visibility === 'MEMBER')

/**
 * Is checkout button disabled
 * @param consentToTerms
 * @param cart
 * @param formCompleted
 * @param paymentRequired
 * @param shippingDetails
 * @param paymentType
 * @returns {boolean}
 */
export const isBtnDisabled = ({
  consentToTerms,
  cart,
  formCompleted,
  paymentRequired,
  shippingDetails,
  paymentType,
}: {
  consentToTerms: boolean
  cart: Transformed.Cart
  formCompleted: boolean
  paymentRequired: boolean
  shippingDetails?: ShippingDetailsT
  paymentType?: string
}): boolean => {
  const consentOrCheckoutBlocked = !consentToTerms || checkoutBlocked(cart)
  const errorAndPaymentRequired = !formCompleted && paymentRequired
  let disabled = consentOrCheckoutBlocked || errorAndPaymentRequired

  if (shippingDetails) {
    disabled = disabled
      || shippingDetails.address === ''
      || shippingDetails.city === ''
      || (shippingDetails.state === '' && paymentType === PaymentMethodType.affirm)
  }
  /**
   * If we have a discount amount and the cart total is 0
   * then assume we have a 100% discount, and we don't need to
   * lock the purchase button
   *
   * Note: if payment is not required, hasInputError
   * will be true but no card number is needed to proceed with purchase.
   */

  if (!paymentRequired) {
    disabled = consentOrCheckoutBlocked
  }

  return disabled
}

/**
 * Returns true if the user is removing all items from the cart.
 *
 * @param {number} cartLineItemsTotal - The total number of line items in the cart.
 * @param {number} lineItemsToDeleteTotal - The total number of line items to be deleted.
 * @param {number} localLineItemsTotal - The total number of line items stored locally.
 *
 * @returns {boolean} - A boolean indicating whether the user is removing all items from the cart.
 */
export const removingAllItems = (
  cartLineItemsTotal: number,
  lineItemsToDeleteTotal: number,
  localLineItemsTotal: number,
): boolean => cartLineItemsTotal > 0
&& cartLineItemsTotal === lineItemsToDeleteTotal
  && localLineItemsTotal === 0

/**
 * Returns the total quantity across all provided line items.
 *
 * @param {Transformed.LineItem[]} lineItems - The line items to total.
 *
 * @returns {number} - The combined quantity of the provided line items.
 */
export const getLineItemsTotal = (lineItems: Transformed.LineItem[]): number => (
  lineItems
    ? lineItems.reduce((acc: number, cur: Transformed.LineItem) => acc + cur.quantity, 0)
    : 0
)

export const getCartLineItemsTotal = (lineItems: readonly Transformed.LineItem[]): number => (
  lineItems
    ? lineItems.reduce((acc: number, cur: Transformed.LineItem) => acc + cur.quantity, 0)
    : 0
)

/**
 * Calculates the total values for different sets of line items and returns the results.
 *
 * @param {Object} params - The parameters for calculating totals.
 * @param {Transformed.LineItem[]} params.localLineItems - An array of line items representing the local line items.
 * @param {Transformed.LineItem[]} params.cartLineItems - An array of line items representing the cart line items.
 * @param {Transformed.LineItem[]} params.lineItemsToDelete - An array of line items that are marked for deletion.
 * @returns {Object} An object containing the calculated totals for the provided sets of line items.
 * @returns {number} return.localLineItemsTotal - The total value of the local line items.
 * @returns {number} return.cartLineItemsTotal - The total value of the cart line items.
 * @returns {number} return.lineItemsToDeleteTotal - The total value of the line items marked for deletion.
 */
export const getTotals = ({
  localLineItems,
  cartLineItems,
  lineItemsToDelete,
}: {
  localLineItems: Transformed.LineItem[]
  cartLineItems: readonly Transformed.LineItem[]
  lineItemsToDelete: Transformed.LineItem[]
}): {
  localLineItemsTotal: number
  cartLineItemsTotal: number
  lineItemsToDeleteTotal: number
} => {
  const localLineItemsTotal = getLineItemsTotal(localLineItems)
  const cartLineItemsTotal = getCartLineItemsTotal(cartLineItems)
  const lineItemsToDeleteTotal = getLineItemsTotal(lineItemsToDelete)

  return {
    localLineItemsTotal,
    cartLineItemsTotal,
    lineItemsToDeleteTotal,
  }
}

/**
 * Calculates the total quantity of line items from an array of groups,
 * where each group contains items with associated metadata.
 *
 * @param {LocalLineItemWithMeta[]} groups - An array of objects representing groups of line items.
 * Each group contains a collection of items and their metadata information.
 *
 * @returns {number} The total quantity of line items across all groups.
 */
export const getQtyForLineItemsWithMeta = (groups: LocalLineItemWithMeta[]): number => (
  groups.reduce((count, group) => {
    let i = 0
    group.items.forEach((item) => {
      i += item.quantity
    })
    return count + i
  }, 0)
)

/**
 * Calculates the current quantity of items for a given category from local
 * and cart line items minus line items to delete.
 * @param {Object} options - The options object.
 * @param {string} options.id - The category ID.
 * @param {Transformed.LineItem[]} options.localLineItems - Array of local line items.
 * @param {Transformed.LineItem[]} options.cartLineItems - Array of cart line items.
 * @param {Transformed.LineItem[]} options.lineItemsToDelete - Array of line items to delete.
 * @returns {number} The total quantity of items in the category.
 */
export const getUnsettledQtyForCategory = ({
  id,
  localLineItems,
  cartLineItems,
  lineItemsToDelete,
}: {
  id: string
  localLineItems: Transformed.LineItem[]
  cartLineItems: readonly Transformed.LineItem[]
  lineItemsToDelete: Transformed.LineItem[]
}): number => {
  const cartQty = getCategoryQty(cartLineItems, id)
  const localQty = getCategoryQty(localLineItems, id)
  const toDeleteQty = getCategoryQty(lineItemsToDelete, id)
  return cartQty + localQty - toDeleteQty
}

/**
 * Constructs a payload for modifying the cart, handling both additions and deletions.
 *
 * Due to API behavior, removing a single ticket from a line item removes that entire
 * line item's reservation. Therefore, when a deletion is requested, this function
 * extracts ALL ticket IDs from the affected cart line item to be completely removed,
 * and calculates the surviving quantity to be re-reserved alongside any newly added
 * local line items.
 *
 * @param {Object} params - The configuration object for building the payload.
 * @param {readonly Transformed.LineItem[]} [params.cartLineItems=[]] - The line items currently existing in the cart.
 * @param {Transformed.LineItem[]} [params.lineItemsToDelete=[]] - The line items the user has requested to remove.
 * @param {Transformed.LineItem[]} [params.localLineItems=[]] - New line items the user is attempting to add to the cart.
 * * @returns {Object} The compiled modification payload to be sent to the API.
 * @returns {string[]} return.ticketIdsToRemove - Array of ticket IDs to be completely removed from the cart.
 * @returns {Transformed.LineItem[]} return.itemsToReserve - Array of line items to reserve (combines new additions and survivors of partial deletions).
 * @returns {boolean} return.hasModifications - Indicates if any modifications (removals or additions) are present in the payload.
 */
export const buildCartModificationPayload = ({
  cartLineItems = [],
  lineItemsToDelete = [],
  localLineItems = [],
}: {
  cartLineItems?: readonly Transformed.LineItem[]
  lineItemsToDelete?: Transformed.LineItem[]
  localLineItems?: Transformed.LineItem[]
}): {
  ticketIdsToRemove: string[];
  itemsToReserve: Transformed.LineItem[];
  hasModifications: boolean;
} => {
  const ticketIdsToRemove: string[] = []
  const itemsToReReserve: Transformed.LineItem[] = []

  if (lineItemsToDelete.length > 0) {
    lineItemsToDelete.forEach((deleteItem) => {
      const cartItem = cartLineItems.find(
        (ci) => ci.category.id === deleteItem.category.id
          && ci.tier.id === deleteItem.tier.id
          && (ci.timeslot ? ci.timeslot.id === deleteItem.timeslot?.id : true),
      )
      if (!cartItem) return

      // Remove ALL tickets from this cart line item
      const inCartTickets = cartItem.tickets.filter((t) => t.inCart)
      ticketIdsToRemove.push(...inCartTickets.map((t) => t.id))

      // Re-reserve survivors
      const surviveQty = cartItem.quantity - deleteItem.quantity
      if (surviveQty > 0) {
        itemsToReReserve.push({ ...cartItem, quantity: surviveQty })
      }
    })
  }

  const itemsToReserve = [...localLineItems, ...itemsToReReserve]
  const hasModifications = ticketIdsToRemove.length > 0 || itemsToReserve.length > 0

  return {
    ticketIdsToRemove,
    itemsToReserve,
    hasModifications,
  }
}
